// @flow
import { useEffect } from 'react';
import Debug from 'debug';
import OlTranslate from 'ol/interaction/Translate';
import type { Feature, Layer, Collection, TranslateInteraction, TranslateEvent } from '../types';
import { useMap, useFeatureListeners } from '../context';
import { useLayer } from '../layers/context';

const debug = Debug('amms:modules:openlayers:interactions:select');

type Props = $ReadOnly<{
    label?: string,
    features?: Collection,
    filter: (feature: Feature, layer: Layer) => boolean,

    onTranslateStart?: (e: TranslateEvent) => void,
    onTranslating?: (e: TranslateEvent) => void,
    onTranslateStart?: (e: TranslateEvent) => void
}>;

function Translate({ label = '', features, filter, onTranslateStart, onTranslating, onTranslateEnd }: Props) {
    const map = useMap();
    const layer = useLayer();
    const featureListeners = useFeatureListeners();

    useEffect(() => {
        let translate: TranslateInteraction;
        let _onTranslateStart;
        let _onTranslating;
        let _onTranslateEnd;

        if(map) {
            if(layer) {
                debug(`Adding translate interaction ${label} to layer: `, layer);
            }
            else {
                debug(`Adding translate interaction ${label} to map`);
            }

            translate = new OlTranslate({
                features,
                filter,
                layers: layer ? [layer] : undefined
            });
            translate.set('label', label);

            // const originalHandleEvent = translate.handleEvent;
            // translate.handleEvent = function() {
            //     originalHandleEvent.apply(this, arguments);
            //
            //     // true, not to stop event propagation.
            //     return true;
            // };

            map.addInteraction(translate);

            const handleTranslate = (type) => (e, translatedFeatures) => {
                const listeners = featureListeners[type];

                if(listeners) {
                    let layerFeatures;
                    if(layer) {
                        const source = layer.getSource();
                        layerFeatures = source ? source.getFeatures() : [];
                    }

                    const filter = (feature) => !layerFeatures || layerFeatures.indexOf(feature) !== -1;

                    if(translatedFeatures) {
                        for(let i = 0, length = translatedFeatures.length; i < length; i += 1) {
                            const feature = translatedFeatures[i];
                            const listener = listeners.get(feature);

                            if(listener && filter(feature)) {
                                listener(e);
                            }
                        }
                    }
                }

                if(onTranslateStart) {
                    onTranslateStart(e);
                }
            };

            _onTranslateStart = (e) => {
                handleTranslate('translateStart')(e, e.features.getArray());
            };
            _onTranslating = (e) => {
                handleTranslate('translating')(e, e.features.getArray());
            };
            _onTranslateEnd = (e) => {
                handleTranslate('translateEnd')(e, e.features.getArray());
            };
            translate.on('translatestart', _onTranslateStart);
            translate.on('translating', _onTranslating);
            translate.on('translateend', _onTranslateEnd);
        }

        return () => {
            if(map && translate) {
                if(layer) {
                    debug(`Removing translate interaction ${label} from layer: `, layer);
                }
                else {
                    debug(`Removing translate interaction ${label} from map`);
                }

                translate.un('translatestart', _onTranslateStart);
                translate.un('translating', _onTranslating);
                translate.un('translateend', _onTranslateEnd);

                map.removeInteraction(translate);
            }
        };
    }, [label, map, layer, features, featureListeners, onTranslateStart, onTranslating, onTranslateEnd]);

    return null;
}

export default Translate;